import {
    Component,
    ViewEncapsulation,
    OnInit,
    inject,
    Input,
    Output,
    EventEmitter,
} from "@angular/core";
import { CerrixInputValidationRegistryService } from "@app/shared/directives/cerrix-input-fields/cerrix-input-validation-registry.service";
import { TranslatePipe, injectTranslation, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/assetmanagement/i18n/scope";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { formatIsoDate } from "@methods/CommonMethods";
import { CerrixInstructionsIconComponent } from "@app/shared/cerrix-instructions-icon/cerrix-instructions-icon.component";
import { AssetManagementModel } from "@models/assetmanagement/assetmanagementmodel";
import { UpdateMetaDataModel } from "@models/assetmanagement/updatemetadatamodel";
import { CerrixButtonComponent } from "@app/shared/atoms/cerrix-button/cerrix-button.component";
import { finalize, Observable, firstValueFrom } from "rxjs";
import { Pages } from "@constants/pages/Pages";
import { TabService } from "@services/tabs/TabService";
import { ToastrService } from "ngx-toastr";
import {
    AssetManagementService,
    AssetProjection,
} from "@app/assetmanagement/services/assetmanagement-data.service";
import {
    CERRIX_SLIDE_OUT_EDIT_ITEM_SECTION,
    CerrixSlideOutEditItemSection,
} from "@app/shared/organisms/cerrix-slide-out-edit-item/models/cerrix-slide-out-edit-item-section.model";
import { CerrixDatepickerGroupComponent } from "@app/shared/molecules/cerrix-datepicker-group/cerrix-datepicker-group.component";
import { LifecycleModel } from "@models/assetmanagement/lifecyclemodel";

@Component({
    selector: "app-edit-asset-lifecycle-information",
    templateUrl: "./edit-asset-lifecycle-information.component.html",
    styleUrls: ["./edit-asset-lifecycle-information.component.scss"],
    imports: [
        TranslatePipe,
        CerrixInstructionsIconComponent,
        CerrixDatepickerGroupComponent,
        CerrixButtonComponent,
    ],
    viewProviders: [provideTranslationScope(scope)],
    providers: [
        CerrixInputValidationRegistryService,
        {
            provide: CERRIX_SLIDE_OUT_EDIT_ITEM_SECTION,
            useExisting: EditAssetLifecycleInformationComponent,
        },
    ],
    encapsulation: ViewEncapsulation.None,
})
export class EditAssetLifecycleInformationComponent
    implements OnInit, CerrixSlideOutEditItemSection
{
    @Input() model: AssetManagementModel;

    @Output() modelChange: EventEmitter<AssetManagementModel> =
        new EventEmitter<AssetManagementModel>();
    @Output() closeEdit: EventEmitter<void> = new EventEmitter<void>();

    protected editModel: UpdateMetaDataModel;

    protected editSaving = false;

    private _ds = inject(AssetManagementService);
    private _validationRegistry = inject(CerrixInputValidationRegistryService);
    private _promptService = inject(CerrixPromptService);
    private _translation = injectTranslation(scope);
    private _tabService = inject(TabService);
    private _pages = inject(Pages);
    private _toastService = inject(ToastrService);

    async ngOnInit() {
        // Before the await: the slide-out can call hasUnsavedChanges() while the scope loads.
        this.revertChanges();

        // t() needs the scope loaded first; before ready() it throws in development.
        await this._translation.ready();
    }

    public hasUnsavedChanges(): boolean {
        return (
            formatIsoDate(this.model.lifecycle?.dateInUse) !==
                formatIsoDate(this.editModel.lifecycle.dateInUse) ||
            formatIsoDate(this.model.lifecycle?.dateOutOfUse) !==
                formatIsoDate(this.editModel.lifecycle.dateOutOfUse)
        );
    }

    public async save(): Promise<boolean> {
        if (!this.validateFields()) {
            return false;
        }

        this.editSaving = true;
        const savingPrompt = this._promptService.loader(
            this._translation.t("detail.savingChanges"),
        );
        const storedAsset = await firstValueFrom(
            this.editSaveSection().pipe(
                finalize(() => {
                    savingPrompt.close();
                    this.editSaving = false;
                    this._tabService.refresh(this._pages.AssetManagementOverview);
                }),
            ),
        );

        if (storedAsset?.guid) {
            this.model = Object.assign(new AssetManagementModel(), storedAsset);
            this.modelChange.emit(this.model);
            this.revertChanges();

            this._toastService.success(
                this._translation.t("detail.updateCompleted"),
            );
            return true;
        }
        return false;
    }

    private validateFields(): boolean {
        // A field the user never touched has never validated itself, so without this a missing
        // required value would pass unnoticed.
        this._validationRegistry.runValidators();

        if (this._validationRegistry.hasAnyErrors()) {
            this._toastService.warning(
                this._validationRegistry
                    .allMessages()
                    .map(
                        (e) =>
                            this._translation.t(
                                "detail.validationMessagePrefix",
                            ) + e,
                    )
                    .join("\n"),
                this._translation.t("detail.saveFailed"),
            );
            return false;
        }
        return true;
    }

    private editSaveSection(): Observable<AssetProjection> {
        return this._ds.vbetaUpdateAssetLifecycle(this.model.guid, {
            dateInUse: formatIsoDate(this.editModel.lifecycle.dateInUse),
            dateOutOfUse: formatIsoDate(this.editModel.lifecycle.dateOutOfUse),
            version: this.editModel.version,
        });
    }

    private revertChanges(): void {
        this.editModel = Object.assign(new UpdateMetaDataModel(), this.model);
        this.editModel.lifecycle = Object.assign(new LifecycleModel(), this.model.lifecycle);
    }
}
